export const DEFAULT_SWA_API_TIMEOUT_MS = 42_000

type TimedSignal = {
  cleanup: () => void
  signal?: AbortSignal
}

export function isRelativeApiRequest(request: unknown): boolean {
  return typeof request === 'string' && !/^[a-z][a-z\d+.-]*:\/\//i.test(request)
}

/**
 * Relative calls use the Static Web Apps API proxy and must finish before its 45-second limit.
 * Absolute URLs, including Durable status and blob URLs, keep their explicitly configured policy.
 */
export function resolveRequestTimeout(
  request: unknown,
  configuredTimeoutMs: number | undefined,
): number | undefined {
  if (!isRelativeApiRequest(request)) return configuredTimeoutMs

  return Math.min(configuredTimeoutMs ?? DEFAULT_SWA_API_TIMEOUT_MS, DEFAULT_SWA_API_TIMEOUT_MS)
}

export function createTimedSignal(
  sourceSignal: AbortSignal | undefined,
  timeoutMs: number | undefined,
): TimedSignal {
  if (!timeoutMs || timeoutMs <= 0) {
    return { signal: sourceSignal, cleanup: () => undefined }
  }

  const controller = new AbortController()
  const forwardAbort = () => controller.abort(sourceSignal?.reason)

  if (sourceSignal?.aborted) {
    forwardAbort()
  } else {
    sourceSignal?.addEventListener('abort', forwardAbort, { once: true })
  }

  const timeoutId = setTimeout(() => {
    const timeoutError = new Error('The request timed out.')
    timeoutError.name = 'TimeoutError'
    controller.abort(timeoutError)
  }, timeoutMs)

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId)
      sourceSignal?.removeEventListener('abort', forwardAbort)
    },
  }
}
